
import { db, serverTimestamp } from "../../firebaseClient";
import type { BrandAssetDoc } from "../types/legalOps";
import * as XLSX from "xlsx";

export const BRAND_ASSET_CSV_HEADERS = [
  'brandName',
  'category',
  'segment',
  'type',
  'region',
  'owner',
  'status',
  'expiryDate',
  'note'
];

const escapeCsv = (val: any) => {
  if (val === undefined || val === null) return "";
  const s = String(val).replace(/"/g, '""');
  return /[",\n\r]/.test(s) ? `"${s}"` : s;
};

function sanitizeString(v: any): string {
  if (v === undefined || v === null) return "";
  return String(v).trim();
}

// Helper to remove undefined values from payload
function sanitizeForFirestore<T extends Record<string, any>>(data: T): Partial<T> {
  const sanitized: Partial<T> = {};
  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined) {
      sanitized[key as keyof T] = value;
    }
  }
  return sanitized;
}

export async function exportBrandAssetsToCsv(): Promise<string> {
  const snapshot = await db.collection("brand_assets").get();
  const assets = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as BrandAssetDoc));

  const headerRow = BRAND_ASSET_CSV_HEADERS.join(",");
  
  const rows = assets.map(item => {
    return BRAND_ASSET_CSV_HEADERS.map(header => {
      let val: any = "";
      switch (header) {
        case 'brandName': val = item.name; break;
        case 'category': val = item.group; break; // Legacy map: group was main category
        case 'segment': val = item.category; break; 
        case 'type': val = item.type; break;
        case 'region': val = item.region; break;
        case 'owner': val = item.owner; break;
        case 'status': val = item.status; break;
        case 'expiryDate': val = item.expiryDate || ""; break;
        case 'note': val = item.notes; break; 
      }
      return escapeCsv(val);
    }).join(",");
  });

  return "\uFEFF" + [headerRow, ...rows].join("\r\n");
}

// Upsert Logic per row
async function upsertBrandAssetFromCsvRow(row: any, existingDocs: BrandAssetDoc[]): Promise<{ status: 'created' | 'updated' | 'skipped', doc?: BrandAssetDoc }> {
    // 1. Map Keys
    const rowKeys = Object.keys(row);
    const getVal = (keys: string[]) => {
        const foundKey = rowKeys.find(k => keys.includes(k.trim()));
        return foundKey ? row[foundKey] : undefined;
    };

    const brandName = sanitizeString(getVal(['brandName', '名稱', '品牌名稱', 'Brand Name']));
    if (!brandName) return { status: 'skipped' };

    // Build Payload with strict mapping to new fields
    // brandName,category,segment,type,region,owner,status,expiryDate,note
    
    // Auto-fill group for backward compatibility based on new classification logic rules if needed,
    // but here we just map fields directly as requested.
    const category = sanitizeString(getVal(['category', '類別', 'Category']));
    const segment = sanitizeString(getVal(['segment', '分群', 'Segment']));

    const rawPayload: any = {
        name: brandName,
        category: category, 
        segment: segment, // New field in DB if needed, or map to existing
        // We will store 'category' from CSV into 'category' field in DB (previously used for 'segment')
        // And 'segment' from CSV into 'segment' field in DB
        
        // For 'group' field (legacy), we can populate it if empty, or leave it.
        // Let's populate it with 'category' value if it looks like a group (House Brand etc)
        group: category || segment || "自有品牌", // Fallback

        type: sanitizeString(getVal(['type', '類型', 'Type'])),
        region: sanitizeString(getVal(['region', '區域', '地區', 'Region'])),
        owner: sanitizeString(getVal(['owner', '負責人', 'Owner', '負責單位'])),
        status: sanitizeString(getVal(['status', '狀態', 'Status'])) || '有效',
        expiryDate: sanitizeString(getVal(['expiryDate', '到期日', 'Expiry Date'])),
        notes: sanitizeString(getVal(['note', 'notes', '備註', 'Notes'])),
        updatedAt: serverTimestamp(),
        lastUpdatedBy: 'Import'
    };

    const payload = sanitizeForFirestore(rawPayload);

    // 2. Duplicate Check (Unique Key: Name + Type + Region)
    const existing = existingDocs.find(d => 
        d.name === payload.name && 
        d.type === payload.type && 
        d.region === payload.region
    );

    if (existing && existing.id) {
        // Use set with merge to update safely
        await db.collection("brand_assets").doc(existing.id).set(payload, { merge: true });
        return { status: 'updated', doc: { ...existing, ...payload } };
    } else {
        // Create new
        payload.createdAt = serverTimestamp();
        // Generate Asset No if not present
        if (!payload.assetNo) {
             payload.assetNo = `BA-${Date.now()}-${Math.floor(Math.random()*1000)}`;
        }
        // Ensure defaults for required fields
        if (!payload.status) payload.status = "有效";
        if (!payload.group) payload.group = "自有品牌"; // Default group if everything is empty

        const ref = await db.collection("brand_assets").add(payload);
        
        const newDoc: BrandAssetDoc = {
            id: ref.id,
            assetNo: payload.assetNo!,
            name: payload.name || "",
            group: payload.group,
            type: payload.type || "",
            region: payload.region || "",
            status: payload.status,
            attachments: [],
            // Optional fields
            category: payload.category,
            owner: payload.owner,
            expiryDate: payload.expiryDate,
            notes: payload.notes,
            lastUpdatedBy: payload.lastUpdatedBy,
            updatedAt: payload.updatedAt,
            createdAt: payload.createdAt
        };

        return { status: 'created', doc: newDoc };
    }
}

export async function importBrandAssetsFromCsv(file: File): Promise<{ created: number; updated: number; skipped: number; }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: "array" });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows: any[] = XLSX.utils.sheet_to_json(firstSheet);

        if (rows.length === 0) {
            throw new Error("CSV 檔案內容為空或無法解析");
        }

        let created = 0;
        let updated = 0;
        let skipped = 0;

        const existingSnap = await db.collection("brand_assets").get();
        const existingDocs = existingSnap.docs.map(d => ({id: d.id, ...d.data()} as BrandAssetDoc));

        for (const row of rows) {
           const result = await upsertBrandAssetFromCsvRow(row, existingDocs);
           if (result.status === 'created') created++;
           else if (result.status === 'updated') updated++;
           else skipped++;
        }
        resolve({ created, updated, skipped });
      } catch(err: any) {
        console.error(err);
        reject(new Error("CSV 解析失敗: " + (err.message || "格式錯誤")));
      }
    };
    reader.onerror = () => reject(new Error("檔案讀取失敗"));
    reader.readAsArrayBuffer(file);
  });
}
